import React, { useState, useEffect } from "react";
import "../../SpearParts/Layouts/Layouts.css";
import { Link, useNavigate } from "react-router-dom";
import {
  getCompleteData,
  getItems,
  getYears,
  searchPart,
} from "../../../pages/Others/Home/OtherHome";
import Axios from "../../../axios/axios";
import { toast } from "react-toastify";
import Select from "react-select";

const BldMiddle = ({ initialFilters = {} }) => {
  const [otherModels, setOtherModels] = useState([]);
  const [otherBrand, setOtherBrand] = useState([]);
  const [otherCategory, setOtherCategory] = useState([]);
  const [region, setRegion] = useState([]);
  const [years, setYears] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState(
    initialFilters.brandId || ""
  );
  const [selectedManufacturer, setSelectedManufacturer] = useState(
    initialFilters.manufacturerId || ""
  );
  const [selectedCategory, setSelectedCategory] = useState(
    initialFilters.categoryId && initialFilters.categoryId !== "undefined"
      ? initialFilters.categoryId
      : ""
  );

  const navigate = useNavigate();
  const categoryOptions = otherCategory.map((cat) => ({
    value: cat._id,
    label: cat.name,
  }));
  const brandOptions = otherBrand.map((brand) => ({
    value: brand._id,
    label: brand.name,
    categoryId: brand.categoryId?._id || brand.categoryId
  }));

  // Filter brand options based on selected category
  const filteredBrandOptions = selectedCategory
    ? brandOptions.filter((opt) => String(opt.categoryId) === String(selectedCategory))
    : brandOptions;

  const manufacturerOptions = manufacturers.map((m) => ({
    value: m._id,
    label: m.manufacturerName,
  }));

  useEffect(() => {
    getCompleteData(setOtherModels, setOtherBrand, setOtherCategory);
    getItems(setRegion);
    setYears(getYears());
    fetchManufacturers();
  }, []);

  useEffect(() => {
    if (selectedBrand) getModelData(selectedBrand);
  }, [selectedBrand]);

  const fetchManufacturers = async () => {
    try {
      const res = await Axios.get("/manufacturer");
      setManufacturers(res.data);
    } catch (error) {
      console.error("Failed to fetch manufacturers:", error);
    }
  };

  const getModelData = async (brandId) => {
    if (!brandId) {
      setOtherModels([]);
      return;
    }
    try {
      const res = await Axios.post("/model/modelBybrand", { brand: brandId });
      setOtherModels(res.data);
    } catch (error) {
      console.error(error);
      setOtherModels([]);
    }
  };

  const handleSearch = () => {
    const filters = {
      brandId: selectedBrand || undefined,
      manufacturerId: selectedManufacturer || undefined,
      categoryId: selectedCategory || undefined,
    };

    if (!filters.brandId && !filters.manufacturerId && !filters.categoryId) {
      toast.warning("Please select at least one filter.", {
        position: "bottom-center",
        autoClose: 5000,
      });
      return;
    }

    const searchParams = new URLSearchParams(filters).toString();

    // 🔍 Find selected category object
    const selectedCatObj = otherCategory.find(
      (cat) => cat._id === filters.categoryId
    );

    if (filters.categoryId && filters.categoryId !== "undefined") {
      // 👉 Check if category has subcategories
      if (
        selectedCatObj &&
        selectedCatObj.subcategories &&
        selectedCatObj.subcategories.length > 0
      ) {
        // 🚀 Navigate to Subcategory Page
        navigate(`/others/subcategories?${searchParams}`, {
          state: {
            category: selectedCatObj,
            otherCategory,
            otherBrand,
            otherModels,
            region,
            filters,
          },
        });
      } else {
        // 🚀 Normal Flow: Go to products page
        navigate(`/others/products?${searchParams}`);
      }
    } else {
      // Category not selected → stay on page with updated filters
      navigate({
        pathname: "/others/category",
        search: searchParams.toString(),
        filters,
      });
    }
  };

  const style = {
    fontFamily: "'Poppins', sans-serif",
    position: "relative",
    fontWeight: 700,
    color: "#353C4B",
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: 50,
      minHeight: 50,
      borderRadius: 6,
      borderColor: "#ccc",
      backgroundColor: "#fff",
      paddingLeft: 4,
      fontFamily: "Poppins, sans-serif",
      fontSize: 15,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(30,99,216,0.2)" : "none",
      "&:hover": { borderColor: "#ccc" },
    }),

    menu: (base) => ({
      ...base,
      marginTop: 2,
      borderRadius: 6,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      zIndex: 99999,
      backgroundColor: "#fff",
      position: "absolute",
    }),

    menuList: (base) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
      maxHeight: 200,
    }),

    option: (base, state) => ({
      ...base,
      padding: "10px 12px",
      fontSize: 15,
      backgroundColor: state.isSelected
        ? "#efefef"
        : state.isFocused
          ? "#f3f4f6"
          : "#ffffff",
      color: "#374151",
      cursor: "pointer",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#374151",
    }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      paddingRight: 12,
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    valueContainer: (base) => ({
      ...base,
      paddingLeft: 4,
    }),

    // This is crucial for proper z-index handling
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
  };

  return (
    <div className="Middle-Container">
      <div className="Middle-Content">
        {/* Heading Section */}
        <div className="Middle-Heading-Section">
          <h1 className="Middle-Title">Find Other Building Materials</h1>
          <p className="Middle-Description">
            Whether you're a professional mechanic , finding the right part for
            your vehicle has never been easier.
            <br /> Use our precise search tool to locate the exact spare part
            you need.
          </p>
        </div>

        {/* Search Form */}
        <div className="Middle-Search-Form">
          <div className="Middle-Select-Wrapper">
            <Select
              options={categoryOptions}
              value={
                categoryOptions.find((opt) => opt.value === selectedCategory) ||
                null
              }
              onChange={(selected) => {
                setSelectedCategory(selected ? selected.value : "");
                setSelectedBrand(""); // Reset brand when category changes
                setOtherModels([]); // Reset models
              }}
              placeholder="Select Category"
              styles={customStyles}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Brand */}
          <div className="Middle-Select-Wrapper">
            <Select
              options={filteredBrandOptions}
              value={
                brandOptions.find((opt) => opt.value === selectedBrand) || null
              }
              onChange={(selected) =>
                setSelectedBrand(selected ? selected.value : "")
              }
              placeholder="Select Brand"
              styles={customStyles}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Manufacturer  */}
          <div className="Middle-Select-Wrapper">
            <Select
              options={manufacturerOptions}
              value={
                manufacturerOptions.find(
                  (opt) => opt.value === selectedManufacturer
                ) || null
              }
              onChange={(selected) =>
                setSelectedManufacturer(selected ? selected.value : "")
              }
              placeholder="Select Manufacturer"
              styles={customStyles}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Search Button */}
          <div className="Middle-Button-Wrapper">
            <button onClick={handleSearch} className="Middle-Search-Button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="Middle-Search-Icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BldMiddle;
